import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { Field as FormikField } from 'formik';
import styled from 'styled-components';
import { FieldControl, SingleSelect } from '~/components';
import { useApi, useWorkspace } from '~/contexts';

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

function buildKey(channel) {
  return `${channel.teamId}:${channel.channelId}`;
}

function Option({ channel }) {
  if (!channel) {
    return null;
  }
  return <Wrapper>{channel.teamName ? `${channel.teamName} / ${channel.channelName}` : channel.channelName}</Wrapper>;
}

const MicrosoftTeamsChannelSelect = forwardRef(({ value, disabled, onChange, ...props }, ref) => {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [channels, setChannels] = useState();
  const [loadError, setLoadError] = useState(null);
  const [filterValue, setFilterValue] = useState('');
  const isMountedRef = useRef(false);

  const filteredChannels = useMemo(
    () =>
      _.filter(channels, (channel) => {
        const term = filterValue.toLowerCase();
        return (
          (channel.teamName || '').toLowerCase().includes(term) ||
          (channel.channelName || '').toLowerCase().includes(term)
        );
      }),
    [channels, filterValue],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      let channels = [];
      let error = null;
      try {
        ({ data: channels } = await api.www.workspaces(workspace.id).integrations().microsoft.teams.getChannels());
      } catch ({ message }) {
        error = message || 'Microsoft Teams channels could not be loaded.';
      }

      if (!isMountedRef.current) {
        return;
      }

      setIsLoading(false);
      setLoadError(error);
      setChannels(channels);
    })();
  }, [api, workspace.id]);

  const handleChange = (event) => {
    onChange(_.find(channels, (channel) => buildKey(channel) === event.target.value) ?? null);
  };

  return (
    <SingleSelect
      {...props}
      showEmptyOption={true}
      showFilter={true}
      placeholder="Project Microsoft Teams Channel"
      noOptionsMessage={loadError || 'Add Ruddr to a team in Microsoft Teams first'}
      value={value ? buildKey(value) : ''}
      valueRenderer={<Option channel={value} />}
      disabled={disabled || isLoading}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      onChange={handleChange}
      ref={ref}>
      {filteredChannels?.map((channel) => (
        <option key={buildKey(channel)} value={buildKey(channel)}>
          <Option channel={channel} />
        </option>
      ))}
    </SingleSelect>
  );
});

const FieldMicrosoftTeamsChannelSelect = forwardRef(
  ({ field, form: { setFieldValue, touched, errors }, label, hideErrorMessage, ...props }, ref) => {
    const error = touched[field.name] && errors[field.name];
    return (
      <FieldControl {...{ label, error, hideErrorMessage }}>
        <MicrosoftTeamsChannelSelect
          {...props}
          value={field.value}
          onChange={(channel) => setFieldValue(field.name, channel)}
          name={field.name}
          ref={ref}
        />
      </FieldControl>
    );
  },
);

const FormikFieldMicrosoftTeamsChannelSelect = (props) => (
  <FormikField {...props} component={FieldMicrosoftTeamsChannelSelect} />
);

export default FormikFieldMicrosoftTeamsChannelSelect;
